import { useCallback, useEffect, useRef } from 'react';
import { CardContextState } from '../../../context/card-context';
import { getTransformedPersonalizationData } from '../../../utils';
import { getProjectTypeCode } from '../../../utils/utility/get-project-type-code';
import { TransformedPersonalizationData } from '../../../utils/helper-settings';

interface UseUnsavedChangesParams {
  initializedData: any;
  cardState: CardContextState;
  cardFacesList: CardContextState['cardFacesList'];
  isOneToMany: boolean;
  persistedPersonalizationData?: TransformedPersonalizationData[];
}

export const useUnsavedChanges = ({
  initializedData,
  cardState,
  cardFacesList,
  isOneToMany,
  persistedPersonalizationData,
}: UseUnsavedChangesParams) => {
  const baselinePersonalizationRef = useRef<string | null>(null);
  const previousProjectIdRef = useRef<string | undefined>(initializedData?.project_id);

  useEffect(() => {
    if (previousProjectIdRef.current !== initializedData?.project_id) {
      baselinePersonalizationRef.current = null;
      previousProjectIdRef.current = initializedData?.project_id;
    }
  }, [initializedData?.project_id]);

  const computePersonalizationSnapshot = useCallback(async () => {
    if (!initializedData || cardFacesList.length <= 0) {
      return null;
    }

    const projectTypeCode = getProjectTypeCode(initializedData);
    const transformedData = await getTransformedPersonalizationData(
      { ...cardState },
      projectTypeCode,
      isOneToMany,
      initializedData?.variables?.template_data,
    ).catch(() => null);

    if (!transformedData) {
      return null;
    }

    return JSON.stringify(transformedData);
  }, [initializedData, cardState, cardFacesList, isOneToMany]);

  const hasUnsavedChanges = useCallback(async () => {
    const currentSnapshot = await computePersonalizationSnapshot();

    if (!currentSnapshot) {
      return false;
    }

    if (!baselinePersonalizationRef.current) {
      baselinePersonalizationRef.current = currentSnapshot;
      return false;
    }

    return baselinePersonalizationRef.current !== currentSnapshot;
  }, [computePersonalizationSnapshot]);

  const resetBaselineAfterSave = useCallback((savedPayload?: TransformedPersonalizationData[]) => {
    if (savedPayload) {
      baselinePersonalizationRef.current = JSON.stringify(savedPayload);
    }
  }, []);

  const setInitialBaseline = useCallback(
    async (savedPayload?: TransformedPersonalizationData[]) => {
      if (savedPayload?.length) {
        baselinePersonalizationRef.current = JSON.stringify(savedPayload);
        return;
      }
      if (persistedPersonalizationData?.length) {
        baselinePersonalizationRef.current = JSON.stringify(persistedPersonalizationData);
        return;
      }
      const snapshot = await computePersonalizationSnapshot();
      if (snapshot) {
        baselinePersonalizationRef.current = snapshot;
      }
    },
    [computePersonalizationSnapshot, persistedPersonalizationData],
  );

  return {
    computePersonalizationSnapshot,
    hasUnsavedChanges,
    resetBaselineAfterSave,
    setInitialBaseline,
    baselinePersonalizationRef,
  };
};
